import React from 'react';
import ReactDOM from 'react-dom';
import ReactHintFactory from 'react-hint';
import { useDateNow, useReduxState } from 'hooks';
import { Badge, DisplayFormattedDate, ProgressBar } from 'components/common';
import { Icon } from 'components/common/icons';

import type {
  CourseCompletion,
  CourseProgressDiscrepancyInCompletion
} from 'shared-types';

const ReactHint = ReactHintFactory(React);

interface Props {
  attribute: string;
  completionDate?: string;
  completionStatus?: CourseCompletion['status'];
  completionUpdatedAt?: string;
  discrepancyInCompletion?: CourseProgressDiscrepancyInCompletion;
  expirationDate?: string;
  numberOfTasks: number;
  numberOfVideos: number;
  readingProgress: number;
  showAnswers?: boolean;
  showReading?: boolean;
  showTaskPoints?: boolean;
  showVideos?: boolean;
  taskAnswers: number;
  taskMaxPoints: number;
  taskPoints: number;
  taskPointsProgress: number;
  videoProgress: number;
  videosWatched: number;
}

const CourseProgressTooltip = ({
  attribute,
  completionDate,
  completionStatus,
  completionUpdatedAt,
  discrepancyInCompletion,
  expirationDate,
  numberOfTasks,
  numberOfVideos,
  readingProgress,
  showAnswers,
  showReading,
  showTaskPoints,
  showVideos,
  taskAnswers,
  taskMaxPoints,
  taskPoints,
  taskPointsProgress,
  videoProgress,
  videosWatched
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme, serverConfig } = useReduxState();

  const expired = expirationDate && Date.parse(expirationDate) < dateNow;

  const renderContent = () => (
    <div className="vuo-tooltip__content">
      {completionStatus ? (
        <div className="item">
          <div className="label">
            {completionStatus === 'waiting-for-reviews'
              ? messages.commonSubmitted
              : completionStatus === 'failed'
              ? messages.completionsWasAttempted
              : messages.commonCompleted}
          </div>
          <div className="value">
            <DisplayFormattedDate
              date={completionDate || completionUpdatedAt}
              withTime={serverConfig.analyticsDatesDisplayTime}
            />
          </div>
        </div>
      ) : null}

      {expirationDate ? (
        <div className="item">
          <div className="label">
            {expired ? messages.commonExpired : messages.commonExpiring}
          </div>
          <div
            className="value"
            style={expired ? { color: theme.dangerColor } : {}}
          >
            <DisplayFormattedDate date={expirationDate} />
          </div>
        </div>
      ) : null}

      {showReading && readingProgress !== undefined ? (
        <div className="item">
          <div className="label">
            {messages.tasksReading}
            {discrepancyInCompletion?.readingProgress ? (
              <span className="discrepancy">
                <Icon icon="asterisk" />
              </span>
            ) : null}
          </div>
          <Badge
            isRounded={false}
            size="small"
            text={
              <div className="value">
                {`${Math.round(readingProgress * 100)}%`}
                <ProgressBar
                  color="#555"
                  progress={readingProgress}
                  progressAnimation={false}
                  style={{ margin: '5px 0 5px 8px', width: 60 }}
                />
              </div>
            }
          />
        </div>
      ) : null}

      {showVideos && videoProgress !== undefined ? (
        <div className="item">
          <div className="label">
            {messages.tasksVideos}
            {discrepancyInCompletion?.videoProgress ? (
              <span className="discrepancy">
                <Icon icon="asterisk" />
              </span>
            ) : null}
          </div>
          {numberOfVideos ? (
            <Badge
              isRounded={false}
              size="small"
              text={
                <div className="value">
                  {serverConfig.videoRequirementsType === 'individual'
                    ? `${videosWatched}/${numberOfVideos}`
                    : `${Math.round(videoProgress * 100)}%`}
                  <ProgressBar
                    color="#555"
                    progress={
                      serverConfig.videoRequirementsType === 'individual'
                        ? videosWatched > 0
                          ? videosWatched / numberOfVideos
                          : 0
                        : videoProgress
                    }
                    progressAnimation={false}
                    style={{ margin: '5px 0 5px 8px', width: 60 }}
                  />
                </div>
              }
            />
          ) : (
            <div className="empty">{messages.tasksNoVideos}</div>
          )}
        </div>
      ) : null}

      {showAnswers && numberOfTasks !== undefined ? (
        <div className="item">
          <div className="label">{messages.tasksAnswers}</div>
          {numberOfTasks ? (
            <Badge
              isRounded={false}
              size="small"
              text={
                <div className="value">
                  {`${taskAnswers}/${numberOfTasks}`}
                  <ProgressBar
                    color="#555"
                    progress={taskAnswers / numberOfTasks}
                    progressAnimation={false}
                    style={{ margin: '5px 0 5px 8px', width: 60 }}
                  />
                </div>
              }
            />
          ) : (
            <div className="empty">{messages.tasksNoTasks}</div>
          )}
        </div>
      ) : null}

      {showTaskPoints && taskPointsProgress !== undefined ? (
        <div className="item">
          <div className="label">
            {messages.tasksTaskPoints}
            {discrepancyInCompletion?.taskPointsProgress ? (
              <span className="discrepancy">
                <Icon icon="asterisk" />
              </span>
            ) : null}
          </div>
          <Badge
            isRounded={false}
            size="small"
            text={
              <div className="value">
                {`${Math.round(
                  taskPointsProgress * 100
                )}% (${taskPoints}/${taskMaxPoints})`}
                <ProgressBar
                  color={theme.successStandaloneColor}
                  progress={taskPointsProgress}
                  progressAnimation={false}
                  style={{ margin: '5px 0 5px 8px', width: 60 }}
                />
              </div>
            }
          />
        </div>
      ) : null}

      {discrepancyInCompletion ? (
        <div className="discrepancy-info">
          <span className="discrepancy">
            <Icon icon="asterisk" />
          </span>{' '}
          {messages.tasksUsersCompletionDiscrepancyAlt}
        </div>
      ) : null}

      <style jsx>{`
        .item {
          display: flex;
          padding: 6px 3px;
        }
        .label {
          flex-grow: 1;
          margin-right: 20px;
        }
        .value {
          display: inline-flex;
        }
        .empty {
          color: ${theme.textColorLighter};
        }
        .discrepancy {
          font-size: 0.6rem;
          padding: 0 2px;
          vertical-align: top;
        }
        .discrepancy-info {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 600;
          padding: 10px 0 5px;
        }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(
    <ReactHint
      attribute={attribute}
      autoPosition={true}
      className="vuo-tooltip-alt"
      events={true}
      delay={50}
      onRenderContent={renderContent}
    />,
    document.querySelector('.dynamic-tooltip-root')
  );
};

export default CourseProgressTooltip;
